import type { QueryClient } from '@tanstack/react-query';
import {
  isViewingInternalChatThread,
  tryPlayInternalChatMessageSound,
} from '@/modules/internal-chat/utils/internalChatMessageSound';
import { invalidateScheduling } from '@/modules/scheduling/query/invalidateScheduling';
import { invalidateAppointments } from '@/modules/appointment/query/invalidateAppointments';
import { invalidatePatients } from '@/modules/patient/query/invalidatePatients';
import { invalidateLeads } from '@/modules/lead/query/invalidateLeads';
import { notifyIncomingCall } from '@/modules/calls/incomingCallBus';
import { queryKeys } from '@/shared/query';
import { masterDataPrefixForResource } from '@/shared/query/masterDataStore';
import type {
  RealtimeEvent,
  MessagingConversationUpdatedPayload,
  NotificationCreatedPayload,
  InternalChatMessageCreatedPayload,
  InternalChatThreadUpdatedPayload,
  InternalChatTypingPayload,
  InternalChatPresencePayload,
  MasterDataChangedPayload,
} from './types';

const TYPING_TTL_MS = 3_500;
const typingTimers = new Map<string, number>();

export type RealtimeEventHandlerContext = {
  viewerUserId?: string;
};

export function handleRealtimeEvent(
  queryClient: QueryClient,
  event: RealtimeEvent,
  context?: RealtimeEventHandlerContext
): void {
  switch (event.type) {
    case 'messaging.conversation.updated':
      handleMessagingConversationUpdated(queryClient, event.payload as MessagingConversationUpdatedPayload);
      break;
    case 'notification.created':
      handleNotificationCreated(queryClient, event.payload as NotificationCreatedPayload);
      break;
    case 'internal_chat.message.created':
      handleInternalChatEvent(
        queryClient,
        event.payload as InternalChatMessageCreatedPayload,
        'message',
        context?.viewerUserId
      );
      break;
    case 'internal_chat.thread.updated':
      handleInternalChatEvent(
        queryClient,
        event.payload as InternalChatThreadUpdatedPayload,
        'thread'
      );
      break;
    case 'internal_chat.typing':
      handleInternalChatTyping(queryClient, event.payload as InternalChatTypingPayload);
      break;
    case 'internal_chat.presence':
      handleInternalChatPresence(queryClient, event.payload as InternalChatPresencePayload);
      break;
    case 'scheduling.time_off.changed':
      handleTimeOffChanged(queryClient);
      break;
    case 'scheduling.busy_time.changed':
      void invalidateScheduling(queryClient, ['busyTime']);
      break;
    case 'appointment.changed':
      void invalidateAppointments(queryClient);
      break;
    case 'patient.changed':
      void invalidatePatients(queryClient);
      break;
    case 'referrer.changed':
      void queryClient.invalidateQueries({ queryKey: queryKeys.referrers.all });
      break;
    case 'lead.changed':
      void invalidateLeads(queryClient);
      break;
    case 'contact.changed': {
      const contactId = (event.payload as { id?: string } | undefined)?.id;
      void queryClient.invalidateQueries({ queryKey: queryKeys.calls.all });
      void invalidateLeads(queryClient);
      void invalidatePatients(queryClient);
      if (contactId) notifyIncomingCall(contactId);
      break;
    }
    case 'attendance.changed':
      handleAttendanceChanged(queryClient);
      break;
    case 'overtime.changed':
      handleOvertimeChanged(queryClient);
      break;
    case 'master_data.changed': {
      const resource = (event.payload as MasterDataChangedPayload | undefined)?.resource;
      const prefix = resource ? masterDataPrefixForResource(resource) : undefined;
      if (prefix) {
        void queryClient.invalidateQueries({ queryKey: [...prefix] });
      }
      break;
    }
    default:
      break;
  }
}

function handleMessagingConversationUpdated(
  queryClient: QueryClient,
  payload: MessagingConversationUpdatedPayload
): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.messaging.all });

  const conversationId = payload?.conversationId;
  if (!conversationId) return;

  void queryClient.invalidateQueries({
    queryKey: queryKeys.messaging.conversation(conversationId),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.messaging.messages(conversationId),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.messaging.historySync(conversationId),
  });
}

function handleNotificationCreated(
  queryClient: QueryClient,
  payload: NotificationCreatedPayload
): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.notification.all });
  if (typeof payload?.event === 'string' && payload.event.startsWith('time_off.')) {
    handleTimeOffChanged(queryClient);
  }
}

function handleInternalChatEvent(
  queryClient: QueryClient,
  payload: InternalChatMessageCreatedPayload | InternalChatThreadUpdatedPayload,
  kind: 'message' | 'thread',
  viewerUserId?: string
): void {
  const threadId = payload?.threadId;
  const reason = 'reason' in payload ? payload.reason : undefined;
  if (kind === 'message') {
    tryPlayInternalChatMessageSound(
      queryClient,
      payload as InternalChatMessageCreatedPayload,
      viewerUserId
    );
  }

  const viewingThread = Boolean(threadId && isViewingInternalChatThread(threadId));
  const skipThreadListRefresh =
    viewingThread && (kind === 'message' || reason === 'read');

  if (!skipThreadListRefresh) {
    void queryClient.invalidateQueries({ queryKey: ['internalChat', 'threads'] });
  }
  if (threadId && !viewingThread) {
    void queryClient.invalidateQueries({ queryKey: queryKeys.internalChat.thread(threadId) });
  }
  if (kind === 'message' || reason === 'created' || reason === 'member_add' || reason === 'member_remove') {
    if (!viewingThread) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.internalChat.all });
    }
    if (threadId && !viewingThread) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.internalChat.messages(threadId) });
    }
  }
}

function handleInternalChatTyping(
  queryClient: QueryClient,
  payload: InternalChatTypingPayload
): void {
  const threadId = payload?.threadId;
  const userId = payload?.userId;
  if (!threadId || !userId) return;
  const at = Date.now();
  queryClient.setQueryData(
    queryKeys.internalChat.typing(threadId),
    (current: Record<string, number> | undefined) => ({
      ...(current ?? {}),
      [userId]: at,
    })
  );
  const timerKey = `${threadId}:${userId}`;
  const previous = typingTimers.get(timerKey);
  if (previous) window.clearTimeout(previous);
  const timer = window.setTimeout(() => {
    queryClient.setQueryData(
      queryKeys.internalChat.typing(threadId),
      (current: Record<string, number> | undefined) => {
        if (!current || current[userId] !== at) return current;
        const next = { ...current };
        delete next[userId];
        return next;
      }
    );
    typingTimers.delete(timerKey);
  }, TYPING_TTL_MS);
  typingTimers.set(timerKey, timer);
}

function patchParticipantOnline<T>(value: T, userId: string, online: boolean): T {
  if (!value || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    return value.map((item) => patchParticipantOnline(item, userId, online)) as T;
  }
  const row = value as { participants?: Array<{ id: string; online?: boolean }> };
  if (!Array.isArray(row.participants)) return value;
  return {
    ...row,
    participants: row.participants.map((person) =>
      person.id === userId ? { ...person, online } : person
    ),
  } as T;
}

function handleInternalChatPresence(
  queryClient: QueryClient,
  payload: InternalChatPresencePayload
): void {
  const userId = payload?.userId;
  if (!userId) return;
  const online = Boolean(payload.online);
  queryClient.setQueriesData({ queryKey: ['internalChat', 'threads'] }, (current) =>
    patchParticipantOnline(current, userId, online)
  );
  queryClient.setQueriesData({ queryKey: ['internalChat', 'thread'] }, (current) =>
    patchParticipantOnline(current, userId, online)
  );
}

function handleTimeOffChanged(queryClient: QueryClient): void {
  void invalidateScheduling(queryClient, ['timeOff', 'calendar', 'leave']);
  void queryClient.invalidateQueries({ queryKey: ['me', 'time-off'] });
}

function handleAttendanceChanged(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.attendance.all });
  void queryClient.invalidateQueries({ queryKey: ['me', 'attendance'] });
}

function handleOvertimeChanged(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.overtime.all });
}
